import React from 'react';

import { Button, Divider, Gap, LoadingState, Text } from '@common/components';
import { useQuery } from '@common/hooks';
import {
  SignInEmailFormRecordType,
  SignInPasswordForm,
  SigningInAsHeader,
} from '@identity/components';
import { LOGIN_AUTH_METHODS_QUERY } from '@identity/graphql';
import { useRedirectPath } from '@identity/hooks/use-redirect-path';

type UserConfirmationType = {
  setTitle: (param: string) => void;
  worksAuthInfo: SignInEmailFormRecordType;
  onChangeEmail?: () => void;
  onBackAction?: (action: (() => void) | null) => void;
};

export function SignInPageUserConfirmation({
  worksAuthInfo,
  setTitle,
  onChangeEmail,
  onBackAction,
}: UserConfirmationType) {
  setTitle('Welcome back');

  const { email } = worksAuthInfo;
  const redirectPath = useRedirectPath();

  const { data, loading } = useQuery(LOGIN_AUTH_METHODS_QUERY, {
    variables: { userEmail: email },
    fetchPolicy: 'no-cache',
  });

  // NOTE: both flags default to true while loading and on error. The server enforces
  // eligibility at submit, so this query only decides what renders.
  const hasPasswordAuth = data?.loginAuthMethods.hasPasswordAuth ?? true;
  const hasSsoAuth = data?.loginAuthMethods.hasSsoAuth ?? true;

  const csrfTokenMeta = document.querySelector(
    'meta[name=csrf-token]'
  ) as HTMLMetaElement;
  const csrfParamMeta = document.querySelector(
    'meta[name=csrf-param]'
  ) as HTMLMetaElement;

  if (loading) {
    return <LoadingState align="center" />;
  }

  return (
    <Gap vertical fullWidth>
      {hasPasswordAuth && (
        <SignInPasswordForm
          email={email}
          onChangeEmail={onChangeEmail}
          onBackAction={onBackAction}
        />
      )}

      {hasPasswordAuth && hasSsoAuth && <Divider gutter={16}>Or</Divider>}

      {hasSsoAuth && (
        <>
          {!hasPasswordAuth && (
            <>
              <SigningInAsHeader email={email} onChange={onChangeEmail} />
              <Text className="body-small-book" secondary>
                Please sign in with SSO. Password login is not available.
                Trouble? Contact support.
              </Text>
            </>
          )}

          <form
            action="/works/auth/sso_initiate"
            method="post"
            style={{ width: '100%' }}
          >
            <input type="hidden" name="email" value={email} />
            {redirectPath && (
              <input type="hidden" name="redirect_path" value={redirectPath} />
            )}
            {csrfParamMeta && csrfTokenMeta && (
              <input
                type="hidden"
                name={csrfParamMeta.content}
                value={csrfTokenMeta.content}
              />
            )}
            <Button htmlType="submit" block>
              Sign in with SSO
            </Button>
          </form>
        </>
      )}
    </Gap>
  );
}
